<template>
  <v-container
    id="printOpenEnrollmentSummary"
  >
    <v-progress-linear
      v-if="loading"
      id="printLoader"
      class="hide-at-print"
      height="10"
      indeterminate
    />
    <div
      v-if="openEnrollment"
    >
      <div>
        <div
          v-if="openEnrollment.isRenewal"
          class="text-center display-2 font-weight-bold"
        >
          <v-icon
            color="green"
            large
          >
            mdi-refresh
          </v-icon>
          Renewal
        </div>
        <div class="text-center display-2">
          {{ openEnrollment.broker }}
        </div>
        <div class="text-center display-1">
          {{ openEnrollment.groupName }}
        </div>
        <div class="text-center display-1">
          {{ openEnrollment.brokerGroupYear }}
        </div>
      </div>
      <group-summary
        class="oe-summary-section"
        :expanded="expanded"
        :details="openEnrollment"
        :group-id="groupId"
        :print="print"
      />
      <renewal-summary
        v-if="openEnrollment.isRenewal"
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <pay-group-summary
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <job-class-summary
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <location-summary
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <eligibility-group-summary
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <eligibility-summary
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <cobra-summary
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <deduction-summary
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <enroller-summary
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
      <template-summaries
        class="oe-summary-section page-break-before"
        :expanded="expanded"
        :open-enrollment="openEnrollment"
        :print="print"
      />
    </div>
    <base-material-snackbar
      v-model="displayError"
      type="error"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import TroodyApi from '@/services/troody/TroodyApi'

  export default {
    name: 'PrintOpenEnrollmentSummary',

    components: {
      GroupSummary: () => import('@/views/dashboard/openEnrollments/components/GroupSummary'),
      RenewalSummary: () => import('@/views/dashboard/openEnrollments/components/RenewalSummary'),
      PayGroupSummary: () => import('@/views/dashboard/openEnrollments/components/PayGroupSummary'),
      JobClassSummary: () => import('@/views/dashboard/openEnrollments/components/JobClassSummary'),
      LocationSummary: () => import('@/views/dashboard/openEnrollments/components/LocationSummary'),
      EligibilityGroupSummary: () => import('@/views/dashboard/openEnrollments/components/EligibilityGroupSummary'),
      EligibilitySummary: () => import('@/views/dashboard/openEnrollments/components/EligibilitySummary'),
      CobraSummary: () => import('@/views/dashboard/openEnrollments/components/CobraSummary'),
      DeductionSummary: () => import('@/views/dashboard/openEnrollments/components/DeductionSummary'),
      EnrollerSummary: () => import('@/views/dashboard/openEnrollments/components/EnrollerSummary'),
      TemplateSummaries: () => import('@/views/dashboard/openEnrollments/components/TemplateSummaries'),
    },

    props: {
      openEnrollmentId: {
        type: [String, Number],
        default: null,
      },
      groupId: {
        type: [String, Number],
        default: null,
      },
    },

    data: () => ({
      allowChange: false,
      expanded: true,
      print: true,

      loadingOe: true,
      loadingData: true,
      intervalId: null,
      progressCompleteChecks: 0,

      errorMessage: null,
      displayError: false,
      formErrors: {},

      openEnrollment: null,

      openEnrollmentResource: new TroodyApi('openEnrollment/'),
    }),

    computed: {
      loading () {
        return this.isLoading()
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      initialize () {
        this.loadOe()
        this.startMonitoring()
      },

      startMonitoring () {
        this.progressCompleteChecks = 0
        this.intervalId = setInterval((scope) => {
          this.monitorProgressBars(scope)
        }, 1000, this)
      },

      monitorProgressBars (parentScope) {
        const scope = (parentScope || this)
        this.loadingData = scope.progressBarsRunning()
        if (scope.loadingData) {
          // If data is found to be loading make sure we are monitoring it
          if (!scope.intervalId) {
            scope.startMonitoring()
          }
        } else {
          // No more data is loading, so stop the monitor, and check one more time before opening print dialogue

          // If we are still being monitored
          if (scope.intervalId) {
            // Stop the monitor
            clearInterval(scope.intervalId)
            scope.intervalId = null

            // Double check that all is done
            setTimeout(() => {
              scope.monitorProgressBars(scope)
            }, 1000)
          } else {
            scope.progressCompleteChecks++

            // We ran successfully without an interval, for a couple times, all must be well
            if (scope.progressCompleteChecks > 2) {
              window.print()
              window.close()
            } else {
              // Double check that all is done
              setTimeout(() => {
                scope.monitorProgressBars(scope)
              }, 1000)
            }
          }
        }
      },

      progressBarsRunning () {
        let progressBarCount = 0
        let incompleteBarCount = 0
        const progressBars = document.getElementsByClassName('v-progress-linear')
        if (progressBars) {
          const progressBarArray = Array.prototype.slice.call(progressBars)
          if (progressBarArray) {
            progressBarCount = progressBarArray.length
          }
          // If we have progress bars to count, let's see how many are still not finished
          if (progressBarCount) {
            incompleteBarCount = progressBarArray.filter((item) => { return item.id !== 'printLoader' && window.getComputedStyle(item).display !== 'none' }).length
          }
        }

        // If we actually have progress bars that are still incompl
        const loadingData = (progressBarCount < 10 || incompleteBarCount > 0)
        return loadingData
      },

      isLoading () {
        return (this.loadingOe || this.loadingData)
      },

      loadOe () {
        this.loadingOe = true
        this.openEnrollmentResource.get(this.openEnrollmentId)
          .then(data => {
            if (data) {
              this.openEnrollment = data
            }
            this.loadingOe = false
          })
      },

      showError (error, msg) {
        console.log(error)
        this.errorMessage = (error || msg)
        this.displayError = true
      },

    },
  }
</script>

<style lang="sass">
  .approval-box
    min-width: 400px

  .note-card
    min-width: 400px !important
    margin-top: 0 !important
    margin-bottom: 0 !important

  .oe-summary-title
    text-align: center
    font-size: 24px
    border-top: 1px dashed rgba(0, 0, 0, 0.2)
    border-bottom: 1px dashed rgba(0, 0, 0, 0.2)
    button
      display: none

  .oe-summary-section
    page-break-after: always

  .oe-summary-subsection
    border-right: 5px double rgba(0, 0, 0, 0.2)
    border-top: 1px dashed rgba(0, 0, 0, 0.2)
    border-bottom: 1px dashed rgba(0, 0, 0, 0.2)
    border-radius: 10px
    background-color: rgba(199, 203, 199, 0.1)
    margin-left: -6px
    padding-left: 6px
    margin-right: -6px
    padding-right: 6px
    page-break-inside: avoid

  .approval-group
    page-break-inside: avoid

  @media print
    .hide-at-print
      display: none
</style>
